"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/cn";
import { Link, usePathname } from "@/i18n/navigation";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import {
  IconLeaf,
  IconShield,
  IconInbox,
  IconAlert,
  IconGauge,
  IconChevronRight,
} from "@/components/ui/icons";
import { IconBuilding, IconBell } from "./admin-icons";
import type { ComponentType } from "react";

interface AdminItem {
  href: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  exact?: boolean;
}

/**
 * Chrome del back-office (/admin): sidebar en desktop, barra superior con nav
 * horizontal en mobile. Otros agentes: agreguen rutas de admin acá.
 * `unreadCount` (bandeja de notificaciones — SOS/salida segura/mensaje
 * flaggeado) se calcula en el layout server y se muestra como badge sobre el
 * link a /admin, donde vive la bandeja completa.
 */
export function AdminNav({
  children,
  unreadCount = 0,
}: {
  children: React.ReactNode;
  unreadCount?: number;
}) {
  const t = useTranslations("common");
  const tb = useTranslations("common.brand");
  const ta = useTranslations("admin.properties");
  const pathname = usePathname();

  const items: AdminItem[] = [
    { href: "/admin", label: t("nav.overview"), icon: IconGauge, exact: true },
    { href: "/admin/verification", label: t("nav.verification"), icon: IconShield },
    { href: "/admin/moderation", label: t("nav.moderation"), icon: IconInbox },
    { href: "/admin/incidents", label: t("nav.incidents"), icon: IconAlert },
    { href: "/admin/metrics", label: t("nav.metrics"), icon: IconGauge },
    { href: "/admin/properties", label: ta("navLabel"), icon: IconBuilding },
  ];

  const isActive = (it: AdminItem) =>
    it.exact
      ? pathname === it.href
      : pathname === it.href || pathname.startsWith(it.href + "/");

  return (
    <div className="min-h-dvh md:flex">
      {/* Sidebar — desktop */}
      <aside className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col border-r border-sand-300 bg-bone-50 p-4 md:flex">
        <Link href="/" className="mb-4 flex items-center gap-2 px-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-forest text-bone-50">
            <IconLeaf size={18} />
          </span>
          <span className="font-display text-lg text-ink">{tb("name")}</span>
          <span className="rounded-full bg-sand-200 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-ink-muted">
            {t("nav.admin")}
          </span>
        </Link>
        <Link
          href="/admin"
          className="mb-2 flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-ink-muted hover:bg-sand-100"
          title={t("nav.notifications")}
        >
          <span className="relative flex h-7 w-7 items-center justify-center">
            <IconBell size={18} />
            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[0.6rem] font-semibold text-bone-50">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </span>
          {t("nav.notifications")}
        </Link>
        <nav className="flex flex-1 flex-col gap-1">
          {items.map((it) => {
            const Icon = it.icon;
            const active = isActive(it);
            return (
              <Link
                key={it.href}
                href={it.href}
                className={cn(
                  "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                  active
                    ? "bg-forest-100 text-forest-strong"
                    : "text-ink-muted hover:bg-sand-100 hover:text-ink-soft",
                )}
              >
                <Icon size={19} />
                {it.label}
              </Link>
            );
          })}
        </nav>
        <div className="pt-4">
          <LocaleSwitcher />
        </div>
      </aside>

      {/* Top bar + nav horizontal — mobile */}
      <div className="md:hidden">
        <div className="flex items-center justify-between border-b border-sand-300 bg-bone-50/90 px-4 py-3 backdrop-blur">
          <Link href="/" className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-forest text-bone-50">
              <IconLeaf size={16} />
            </span>
            <span className="font-display text-base text-ink">
              {tb("name")} · {t("nav.admin")}
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link href="/admin" className="relative flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-sand-100">
              <IconBell size={17} />
              {unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-danger px-1 text-[0.55rem] font-semibold text-bone-50">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Link>
            <LocaleSwitcher />
          </div>
        </div>
        <MobileTabs items={items} isActive={isActive} />
      </div>

      <main className="flex-1">{children}</main>
    </div>
  );
}

/**
 * Barra de secciones — mobile: scrollea horizontal (6 secciones no entran en
 * 390px). Sin indicador, un usuario nuevo (ej. en la demo) no descubre que
 * "Métricas"/"Propiedades" existen fuera de pantalla. Acá:
 * - degradados en los bordes que aparecen/desaparecen según cuánto queda
 *   por scrollear (con una flechita a la derecha la primera vez).
 * - la pestaña activa se centra sola al entrar directo a esa ruta (ej. desde
 *   un link o al recargar en /admin/properties), sin depender de que el
 *   usuario la haya scrolleado hasta ahí antes.
 */
function MobileTabs({
  items,
  isActive,
}: {
  items: AdminItem[];
  isActive: (it: AdminItem) => boolean;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const updateFades = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 4);
    setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    updateFades();
    el
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest", inline: "center" });
    window.addEventListener("resize", updateFades);
    return () => window.removeEventListener("resize", updateFades);
  }, [items]);

  return (
    <div className="relative border-b border-sand-300 bg-bone-50">
      <div
        ref={scrollerRef}
        onScroll={updateFades}
        className="flex gap-1 overflow-x-auto px-3 py-2"
      >
        {items.map((it) => {
          const active = isActive(it);
          return (
            <Link
              key={it.href}
              href={it.href}
              data-active={active}
              className={cn(
                "flex shrink-0 items-center whitespace-nowrap rounded-full px-3.5 py-2.5 text-sm font-medium transition-colors",
                active
                  ? "bg-forest-100 text-forest-strong"
                  : "text-ink-muted hover:bg-sand-100",
              )}
            >
              {it.label}
            </Link>
          );
        })}
      </div>
      {showLeftFade && (
        <div className="pointer-events-none absolute left-0 top-0 h-full w-6 bg-gradient-to-r from-bone-50 to-transparent" />
      )}
      {showRightFade && (
        <div className="pointer-events-none absolute right-0 top-0 flex h-full w-9 items-center justify-end bg-gradient-to-l from-bone-50 via-bone-50/90 to-transparent">
          <IconChevronRight size={14} className="text-ink-muted" />
        </div>
      )}
    </div>
  );
}
